import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Download, FileText, Package, Loader2, ArrowLeft, FolderOpen,
  Search, Filter, ChevronRight, ExternalLink, HardDrive
} from 'lucide-react';
import { trackDashboard, trackPageView } from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';
import {
  useMyPurchasedProducts,
  useProductFiles,
  useFileCategories,
  useFileDownload
} from '@/hooks/useMyFiles';

// Format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Product List View
const ProductListView: React.FC = () => {
  const { products, totalProducts, totalFiles, loading, error, refetch } = useMyPurchasedProducts();

  useScrollTracking('DashboardFiles');

  useEffect(() => {
    trackDashboard.view('files');
    trackPageView('DashboardFiles');
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 mb-4">{error}</div>
        <button
          onClick={refetch}
          className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-16">
        <FolderOpen className="w-20 h-20 mx-auto mb-6 text-gray-500" />
        <h3 className="text-2xl font-semibold text-gray-300 mb-3">No Digital Products Yet</h3>
        <p className="text-gray-400 mb-8 max-w-md mx-auto">
          Your purchased digital products will appear here. Get instant access to downloadable resources, templates, and guides.
        </p>
        <Link
          to="/ultimate-prompt-bundle"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-lg hover:shadow-lg hover:shadow-amber-500/50 transition-all"
        >
          <Package className="w-5 h-5" />
          Browse Digital Products
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-slate-800/60 backdrop-blur-sm border border-amber-500/30 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Package className="w-5 h-5 text-amber-400" />
            </div>
            <span className="text-gray-400 text-sm">Total Products</span>
          </div>
          <p className="text-2xl font-bold text-white">{totalProducts}</p>
        </div>

        <div className="bg-slate-800/60 backdrop-blur-sm border border-orange-500/30 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <FileText className="w-5 h-5 text-orange-400" />
            </div>
            <span className="text-gray-400 text-sm">Total Files</span>
          </div>
          <p className="text-2xl font-bold text-white">{totalFiles}</p>
        </div>
      </div>

      {/* Product List */}
      <div className="bg-slate-800/60 backdrop-blur-sm border-2 border-amber-500/30 rounded-2xl p-6 sm:p-8">
        <h2 className="text-2xl font-bold text-white mb-6">Your Digital Products</h2>

        <div className="space-y-4">
          {products.map((product) => (
            <Link
              key={product.id}
              to={`/dashboard/files/${product.slug}`}
              className="flex items-center justify-between p-4 sm:p-6 bg-slate-900/50 border border-white/10 rounded-xl hover:border-amber-500/50 transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-xl">
                  <Package className="w-8 h-8 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white group-hover:text-amber-400 transition-colors">
                    {product.title}
                  </h3>
                  <div className="flex items-center gap-3 mt-1 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      {product.file_count} files
                    </span>
                    <span className="text-gray-600">•</span>
                    <span>Purchased {formatDate(product.purchased_at)}</span>
                  </div>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-gray-500 group-hover:text-amber-400 group-hover:translate-x-1 transition-all" />
            </Link>
          ))}
        </div>
      </div>
    </>
  );
};

// Product Files View
const ProductFilesView: React.FC<{ productSlug: string }> = ({ productSlug }) => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');

  const { product, files, totalFiles, totalSizeBytes, loading, error, refetch } = useProductFiles(productSlug, selectedCategory);
  const { categories } = useFileCategories(productSlug);
  const { downloadFile, downloading } = useFileDownload();

  useScrollTracking('DashboardProductFiles');

  useEffect(() => {
    trackPageView('DashboardProductFiles', { productSlug });
  }, [productSlug]);

  const handleDownload = async (fileId: string, filename: string) => {
    await downloadFile(productSlug, fileId, filename);
  };

  // Filter files by search query
  const filteredFiles = files.filter(file =>
    file.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    file.original_filename.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 mb-4">{error}</div>
        <div className="flex gap-4 justify-center">
          <button
            onClick={refetch}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
          >
            Try Again
          </button>
          <button
            onClick={() => navigate('/dashboard/files')}
            className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Back Button */}
      <button
        onClick={() => navigate('/dashboard/files')}
        className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to My Files
      </button>

      {/* Product Header */}
      <div className="bg-slate-800/60 backdrop-blur-sm border-2 border-amber-500/30 rounded-2xl p-6 sm:p-8 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">{product?.title}</h2>
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <span className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                {totalFiles} files
              </span>
              <span className="flex items-center gap-1">
                <HardDrive className="w-4 h-4" />
                {formatFileSize(totalSizeBytes)}
              </span>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-slate-900/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-500/50 w-full sm:w-64"
            />
          </div>
        </div>

        {/* Category Filter */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-6">
            <button
              onClick={() => setSelectedCategory(undefined)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                !selectedCategory
                  ? 'bg-amber-500 text-white'
                  : 'bg-slate-900/50 text-gray-400 hover:text-white'
              }`}
            >
              All Files
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-amber-500 text-white'
                    : 'bg-slate-900/50 text-gray-400 hover:text-white'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Files List */}
      <div className="bg-slate-800/60 backdrop-blur-sm border-2 border-amber-500/30 rounded-2xl p-6 sm:p-8">
        <h3 className="text-xl font-bold text-white mb-6">Files</h3>

        {filteredFiles.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-500" />
            <p className="text-gray-400">No files found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredFiles.map((file) => (
              <div
                key={file.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-slate-900/50 border border-white/10 rounded-lg hover:border-amber-500/30 transition-all gap-4"
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-amber-500/20 rounded-lg">
                    <FileText className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">{file.title}</h4>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-400">
                      <span>{file.original_filename}</span>
                      <span className="text-gray-600">•</span>
                      <span>{formatFileSize(file.file_size)}</span>
                      {file.category && (
                        <>
                          <span className="text-gray-600">•</span>
                          <span className="px-2 py-0.5 bg-slate-800 rounded text-xs">{file.category}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 sm:ml-auto">
                  {/* Direct download link */}
                  <a
                    href={file.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-gray-300 rounded-lg hover:bg-slate-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span className="hidden sm:inline">Open</span>
                  </a>

                  {/* Download button with tracking */}
                  <button
                    onClick={() => handleDownload(file.id, file.original_filename)}
                    disabled={downloading}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-lg hover:shadow-lg hover:shadow-amber-500/30 transition-all disabled:opacity-50"
                  >
                    <Download className="w-4 h-4" />
                    <span className="hidden sm:inline">Download</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

// Main Component
const DashboardFiles: React.FC = () => {
  const { productSlug } = useParams<{ productSlug?: string }>();

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
          <Download className="w-10 h-10 text-amber-400" />
          My Files
        </h1>
        <p className="text-gray-300 text-lg">
          Access your purchased digital products and downloads
        </p>
      </div>

      {productSlug ? (
        <ProductFilesView productSlug={productSlug} />
      ) : (
        <ProductListView />
      )}
    </div>
  );
};

export default DashboardFiles;
